import React, { useContext, useEffect, useRef, useState } from 'react';
import AIEduButton from '../../components/Button/AIEduButton';
import AIEduDialog from '../../components/Dialog/AIEduDialog';
import AIEduDataTable from '../../components/DataTable/AIEduDataTable';
import AIEduInputTextFixedLabel from '../../components/Input/AIEduInputTextFixedLabel';
import AIEduDropDown from '../../components/DropDown/AIEduDropDown';
import FileUpload from '../../components/FileUpload/FileUpload';
import axiosInstance from '../../services/setup/axiosInstance';
import { useAuthenticate } from '../../hooks';
import { v4 as uuidv4 } from 'uuid';
import greenSuccessCheck from '../../assets/images/green_success_icon.png';
import userFailure from '../../assets/images/userFailureLarge.svg';
import { Context } from '../../context/Context';
import { showErrorToast } from '../../services/setup/ApiResponseHandler';
import { API } from '../../services';
import { downloadCSV, isValidLowerCaseEmailStrict } from '../../utils/Utils';
import { useIntl } from 'react-intl';
import { EMAIL_MAX_LENGTH, REDIRECT_DURATION } from '../../utils/Constants';

const AddUser = ({ setIsAddUserVisible, accessTypeOptions, setLoading }) => {
    const intl = useIntl();
    const { formatMessage: f } = intl;
    const [visible, setVisible] = useState(false);
    const [boxVisible, setBoxVisible] = useState(false);
    const { triggerRefresh, defaultAccessType } = useContext(Context);
    const [users, setUsers] = useState([
        { id: uuidv4(), email: '', role: defaultAccessType, nfcTagId: '' },
    ]);
    const [files, setFiles] = useState([]);
    const [progress, setProgress] = useState(0);
    const [userErrorMessage, setUserErrorMessage] = useState('');
    const [usersCount, setUsersCount] = useState(0);

    const { accountInfo } = useAuthenticate();
    const { organizations, place } = accountInfo ?? {};
    const { placeId } = place ?? {};
    const { organizationId } = organizations?.[0] ?? {};
    const [uploadInProgress, setUploadInProgress] = useState(false);
    const [errorUpload, setErrorUpload] = useState(false);
    const saveBtnRef = useRef(null);
    const [navigationTimeout, setNavigationTimeout] = useState(null);
    const [differentFileFormatError, setDifferentFileFormatError] =
        useState(false);

    const handleDeleteUser = (rowData) => {
        setUsers((prev) => prev.filter((user) => user.id !== rowData.id));
    };

    const UserAddedPopUp = ({
        visible,
        userErrorMessage,
        usersCount,
        navigationTimeout,
        onClose,
        onNavigateToUser,
        f,
    }) => {
        const [counter, setCounter] = useState(10);
        useEffect(() => {
            if (userErrorMessage) return;

            let intervalId = null;
            if (navigationTimeout) {
                intervalId = setInterval(() => {
                    setCounter((prevCount) => prevCount - 1);
                    if (counter < 0) {
                        clearInterval(intervalId);
                    }
                }, 1000);
            }

            return () => clearInterval(intervalId);
        }, [navigationTimeout]);

        return (
            <AIEduDialog
                visible={visible}
                draggable={false}
                style={{ width: '40vw', minWidth: '40rem' }}
                onHide={() => {
                    if (!visible) return;
                    onClose();
                }}
                content={
                    <>
                        <div
                            className='flex justify-content-center mt-2 mb-5'
                            style={{ width: '6rem', margin: 'auto' }}
                        >
                            <img
                                src={
                                    userErrorMessage
                                        ? userFailure
                                        : greenSuccessCheck
                                }
                                style={{
                                    objectFit: 'contain',
                                    height: '100%',
                                    width: '100%',
                                }}
                            />
                        </div>

                        <div
                            className='flex justify-content-center mb-2'
                            style={{
                                fontSize: '24px',
                                fontWeight: '600',
                                textAlign: 'center',
                            }}
                        >
                            {userErrorMessage
                                ? userErrorMessage
                                : `${usersCount} users added successfully`}
                        </div>

                        <div
                            className='flex justify-content-center mb-2'
                            style={{ fontSize: '16px', fontWeight: '500' }}
                        >
                            Go to user dashboard to view
                        </div>

                        <div className='flex justify-content-center mt-6 mb-4'>
                            {!userErrorMessage ? (
                                <AIEduButton
                                    label={f({ id: 'AI_EDU_USER_DASHBOARD' })}
                                    className='btnSecondary'
                                    style={{ width: '15rem' }}
                                    onClick={() => {
                                        clearTimeout(navigationTimeout);
                                        onClose();
                                        onNavigateToUser();
                                    }}
                                />
                            ) : (
                                <AIEduButton
                                    label={f({ id: 'AI_EDU_CLOSE' })}
                                    className='btnSecondary'
                                    style={{ width: '15rem' }}
                                    onClick={() => {
                                        onClose();
                                    }}
                                />
                            )}
                        </div>
                        {!userErrorMessage && (
                            <div
                                className='flex justify-content-center my-3'
                                style={{ fontFamily: 'SamsungOne-500' }}
                            >
                                Redirecting in {counter}
                            </div>
                        )}
                    </>
                }
            />
        );
    };

    const handleChange = (e, rowData) => {
        const { name, value } = e.target;
        setUsers((prev) =>
            prev.map((user) =>
                user.id === rowData.id ? { ...user, [name]: value } : user
            )
        );
    };

    const emailBodyTemplate = (rowData) => {
        return (
            <AIEduInputTextFixedLabel
                type='email'
                name='email'
                style={{ height: '2rem' }}
                value={rowData.email}
                onChange={(e) => handleChange(e, rowData)}
                placeholder='Enter email'
                maxLength={EMAIL_MAX_LENGTH}
            />
        );
    };

    const accessTypeBodyTemplate = (rowData) => {
        return (
            <AIEduDropDown
                className='user-access-type'
                name='role'
                value={rowData.role}
                onChange={(e) => handleChange(e, rowData)}
                options={accessTypeOptions}
                placeholder='Select Access Type'
            />
        );
    };

    const nfcTagTemplate = (rowData) => {
        return (
            <AIEduInputTextFixedLabel
                type='text'
                name='nfcTagId'
                style={{ height: '2rem' }}
                value={rowData.nfcTagId}
                onChange={(e) => handleChange(e, rowData)}
                placeholder='Enter NFC Tag'
            />
        );
    };

    const actionBodyTemplate = (rowData) => (
        <i
            className='pi pi-trash'
            style={{ fontSize: '20px', cursor: 'pointer' }}
            onClick={() => handleDeleteUser(rowData)}
        />
    );

    const columns = [
        {
            field: 'email',
            header: f({ id: 'AI_EDU_EMAIL' }),
            body: emailBodyTemplate,
            minWidth: '20rem',
            width: '25%',
        },
        {
            field: 'nfcTagId',
            header: f({ id: 'AI_EDU_NFC_TAG' }),
            body: nfcTagTemplate,
            minWidth: '20rem',
            width: '25%',
        },
        {
            field: 'role',
            header: f({ id: 'AI_EDU_ACCESS_TYPE' }),
            body: accessTypeBodyTemplate,
            minWidth: '5rem',
            width: '10%',
        },
        { header: 'Action', body: actionBodyTemplate, minWidth: '5rem' },
    ];

    const handleAddUser = () => {
        const ID = uuidv4();
        setUsers([
            ...users,
            { id: ID, email: '', role: defaultAccessType, nfcTagId: '' },
        ]);
    };

    const closeFileUpload = () => (
        <div className='flex justify-content-end mr-4'>
            <i
                style={{ cursor: 'pointer' }}
                className='pi pi-times'
                onClick={resetFileUpload}
            />
        </div>
    );

    const handleRedirect = () => {
        const timeOut = setTimeout(() => {
            setBoxVisible(false);
            setUserErrorMessage('');
            setUsersCount(0);
            navigateToUser();
        }, REDIRECT_DURATION);

        setNavigationTimeout(timeOut);
    };

    const handleFileUpload = () => {
        const formData = new FormData();
        formData.append('files', files[0]);
        setUploadInProgress(true);
        axiosInstance
            .post(`/ums/${organizationId}/${placeId}/user`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (event) => {
                    const percent = Math.round(
                        (event.loaded * 100) / event.total
                    );
                    setProgress(percent);
                },
            })
            .then(({ data }) => {
                setVisible(false);
                setBoxVisible(true);
                handleRedirect();

                const { count } = data ?? {};
                setUsersCount(count);
                setUserErrorMessage('');
            })
            .catch(({ response }) => {
                const { data, status } = response ?? {};
                const { error } = data ?? {};
                setErrorUpload(true);
                if (status === 500) {
                    showErrorToast('Internal Server Error');
                } else {
                    // showErrorToast(error);
                    setBoxVisible(false);
                    setUserErrorMessage(error);
                }
            })
            .finally(() => {
                setUploadInProgress(false);
            });
    };

    const validateUsersData = (usersData) => {
        const emailSet = new Set();
        for (let index = 0; index < usersData.length; index++) {
            const { role, email } = usersData[index];
            const hash = email + '#' + role;
            if (hash === '#') {
                showErrorToast('Please provide all the fields for user');
                return false;
            }
            if (!isValidLowerCaseEmailStrict(email)) {
                showErrorToast('Please enter valid email');
                return false;
            }
            if (emailSet.has(email)) {
                showErrorToast('Duplicate email found.');
                return false;
            }
            if (!role) {
                showErrorToast('Please provide access type for all fields');
                return false;
            }

            emailSet.add(email);
        }

        return true;
    };

    const handleAddUsersManually = () => {
        saveBtnRef.current.blur();

        const usersData = users?.map((user) => ({
            email: user.email.toLowerCase().trim(),
            role: user.role,
            nfcTagId: user.nfcTagId.trim(),
        }));

        if (validateUsersData(usersData)) {
            setLoading(true);
            API.addUsers(organizationId, placeId, usersData)
                .then((res) => {
                    setBoxVisible(true);
                    const { data } = res ?? {};
                    if (res.status !== 201) {
                        const { error } = data ?? {};
                        setUserErrorMessage(error);
                        if (res.status === 500) {
                            setUserErrorMessage('Internal Server Error');
                        }
                    } else {
                        const { count } = data ?? {};
                        setUsersCount(count);
                        setUserErrorMessage('');
                        handleRedirect();
                    }
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    const navigateToUser = () => {
        setIsAddUserVisible(false);
        triggerRefresh('user');
    };

    const resetFileUpload = () => {
        setVisible(false);
        setFiles([]);
        setProgress(0);
        setErrorUpload(false);
        setUploadInProgress(false);
        setDifferentFileFormatError('');
    };

    return (
        <div className='user add-user'>
            <div className='add_user_header'>
                <span
                    onClick={navigateToUser}
                    className='cursor-pointer hover:underline'
                >
                    {f({ id: 'AI_EDU_USER' })}
                </span>
                <i className='pi pi-chevron-right mx-2' />
                <span>Add New User</span>
            </div>
            <div className='mt-2 mb-3 add_user_text'>
                {f({ id: 'AI_EDU_BULK_USER_ADDITION' })}
            </div>
            <div className='flex justify-content-start'>
                <div className='upload_file'>
                    <AIEduButton
                        label={f({ id: 'AI_EDU_UPLOAD_FILE' })}
                        icon='pi pi-upload'
                        className='mr-6 btnPrimary'
                        style={{ width: '12rem' }}
                        onClick={() => setVisible(true)}
                    />
                    <div className='supported_text'>{`(Supported format: .csv | Max rows: 1000 | Max size: 300kb)`}</div>
                </div>
                <AIEduDialog
                    title={closeFileUpload}
                    visible={visible}
                    draggable={false}
                    onHide={() => {
                        if (!visible) return;
                        resetFileUpload();
                    }}
                    style={{ width: '50vw' }}
                    breakpoints={{ '960px': '75vw', '641px': '100vw' }}
                    content={
                        <FileUpload
                            text={f({ id: 'AI_EDU_ADD_ALL_USERS' })}
                            handleFileUpload={handleFileUpload}
                            files={files}
                            setFiles={setFiles}
                            progress={progress}
                            setProgress={setProgress}
                            errorUpload={errorUpload}
                            uploadInProgress={uploadInProgress}
                            setErrorUpload={setErrorUpload}
                            errorMessage={userErrorMessage}
                            setUploadInProgress={setUploadInProgress}
                            differentFileFormatError={differentFileFormatError}
                            setDifferentFileFormatError={
                                setDifferentFileFormatError
                            }
                        />
                    }
                />
                <div
                    className='flex download_file_text'
                    style={{ lineHeight: '40px' }}
                    onClick={() =>
                        downloadCSV(
                            [
                                ['email', 'access type'],
                                ['newUser1@gmail.com', 'admin'],
                                ['newUser2@gmail.com', 'user'],
                                ['newUser3@gmail.com', 'user'],
                            ],
                            'users_format.csv'
                        )
                    }
                >
                    <div className='mr-2'>
                        <i className='pi pi-download' />
                    </div>
                    <div>{f({ id: 'AI_EDU_DOWNLOAD_FILE_FORMAT' })}</div>
                </div>
            </div>

            <div
                className='flex justify-content-between mt-3 mb-2'
                style={{ width: '100%' }}
            >
                <div style={{ fontFamily: 'SamsungOne-400, sans-serif' }}>
                    OR
                </div>
                <div className='text_center_line' />
            </div>

            <div className='mt-3 mb-3 add_user_text'>
                {f({ id: 'AI_EDU_ADD_USERS_MANUALLY' })}
            </div>

            <AIEduDataTable
                data={users}
                columns={columns}
                className='add_user_table'
                scrollHeight='45vh'
            />

            <div className='flex mt-4'>
                <div className='add_more_user-text' onClick={handleAddUser}>
                    <i
                        className='pi pi-plus mr-2'
                        style={{ fontSize: '16px' }}
                    />
                    {f({ id: 'AI_EDU_ADD_MORE' })}
                </div>
            </div>

            <AIEduButton
                label={f({ id: 'AI_EDU_SAVE' })}
                ref={saveBtnRef}
                className='mt-4 mr-6 btnPrimary'
                onClick={handleAddUsersManually}
                disabled={users.length === 0}
            />
            <UserAddedPopUp
                visible={boxVisible}
                userErrorMessage={userErrorMessage}
                usersCount={usersCount}
                navigationTimeout={navigationTimeout}
                onClose={() => {
                    setBoxVisible(false);
                    setUserErrorMessage('');
                    setUsersCount(0);
                }}
                onNavigateToUser={navigateToUser}
                f={f}
            />
        </div>
    );
};

export default AddUser;
